// features/workstations/catalyst/new-prediction-section.js
// New Prediction section — form scaffold for locking a stimulus
// card. Visible but disabled in Phase 1 of UI build (no write
// path yet). Ships in Phase 2 of UI work.

export default function NewPredictionSection({ workstation }) {
  return (
    <div className="cat-section">
      <div className="cat-form-disabled-banner">
        <strong>Form is read-only in Phase 1 of UI.</strong> The write path that
        actually inserts into <code>catalyst_predictions</code> ships in Phase 2 of
        UI work — after the universe filter (build Phase 2), corpus build (Phase 3),
        and pump classifier (Phase 6) are in place. Phase 8 is the methodology Phase
        when the first locked predictions become possible.
      </div>

      <div className="cat-block">
        <div className="cat-block-title">Stimulus card — penny + catalyst sustain</div>
        <div className="cat-block-subtitle">
          The 7-step pre-registration card per <strong>catalyst-v2.0</strong> spec. Once a card is
          locked (button at bottom), the prediction becomes immutable per Pillar 4B
          Rule 1 — no retroactive recalibration. All fields below match the schema in
          <code> docs/PENNY_CATALYST_SUSTAIN_BUILD.md §Stimulus Card v2.0</code>.
        </div>

        <FormBlock title="1. Affected population (point-in-time at event)">
          <FormField label="Ticker"                  placeholder="e.g. ABCD" />
          <FormField label="Market cap (USD, millions)"  placeholder="e.g. 145" />
          <FormField label="Pre-pump close ($)"      placeholder="e.g. 3.42" />
          <FormField label="Sector"                  placeholder="biotech | tech | mining | ma_target | ..." />
          <FormField label="Subsector"               placeholder="e.g. oncology, gold, saas" />
          <FormField label="Exchange"                placeholder="NASDAQ | NYSE | OTCQX | OTCQB" />
          <FormField label="Prior pump count (24mo)" placeholder="0" />
        </FormBlock>

        <FormBlock title="2. Perturbation (the catalyst)">
          <FormField label="Event class" placeholder="fda | contract | ma | earnings | drill | financing | partnership | regulatory | generic-news" />
          <FormField label="Event timestamp (ISO 8601)" placeholder="2026-05-08T14:30:00Z" />
          <FormField label="D+0 intraday move (%)"      placeholder="must be ≥ 50" />
          <FormField label="8-K filing URL (or empty)"  placeholder="https://www.sec.gov/..." />
          <FormField label="PR release URL"             placeholder="https://www.businesswire.com/..." />
          <FormField label="PR wire tier"               placeholder="tier1 | tier2 | tier3" />
          <FormFieldArea label="PR release text (for L3 extraction)" placeholder="Paste the full PR text — extracted to L3 catalyst language features." />
        </FormBlock>

        <FormBlock title="3-5. Response space, time horizon, counterfactual">
          <div style={{ fontSize: 12, color: '#888', lineHeight: 1.6 }}>
            Auto-populated from the methodology spec at lock time:
            <ul style={{ margin: '8px 0 0 16px', padding: 0 }}>
              <li>Response: return_d0/d1/d5/d30 + vol_ratio_d0/d5/d30 + halt_count_d0/5d/30d</li>
              <li>Time horizons: D+0 / D+1 / D+5 / D+30 (locked, primary = D+30)</li>
              <li>Counterfactual: 30+ analogs by 5-band feature similarity (computed by replay simulator, Phase 7)</li>
            </ul>
          </div>
        </FormBlock>

        <FormBlock title="6. Pre-registered outcome">
          <FormField label="Predicted P(D+30 sustain)"  placeholder="e.g. 0.68" />
          <FormField label="Point estimate D+30 return (%)" placeholder="e.g. 42" />
          <FormFieldArea label="Reasoning" placeholder="What features fired, what analog set looks like, what the prediction asserts." />
        </FormBlock>

        <FormBlock title="7. Falsifier">
          <FormFieldArea
            label="Falsifier condition"
            placeholder="Default: D+30 close < pre_pump_close × 1.25 OR D+30 volume < D+0 volume × 0.7 → faded. Edit only with reason."
          />
          <FormField label="Applies to (child hypotheses)" placeholder="e.g. H-CATALYST-002v2, H-CATALYST-006v2" />
        </FormBlock>

        <FormBlock title="Provenance (auto-pinned at lock time)">
          <div style={{ fontSize: 12, color: '#888', lineHeight: 1.6 }}>
            <div>Methodology version: <strong style={{ color: '#c8a96e' }}>catalyst-v2.0</strong></div>
            <div>Library version:     <strong style={{ color: '#c8a96e' }}>v1 (seed)</strong></div>
            <div>Classifier model:    <strong style={{ color: '#888' }}>not yet trained (Phase 6)</strong></div>
          </div>
        </FormBlock>

        <button
          type="button"
          disabled
          style={{
            width: '100%',
            padding: '12px 16px',
            background: 'rgba(200, 169, 110, 0.10)',
            border: '1px solid rgba(200, 169, 110, 0.4)',
            color: '#888',
            borderRadius: 3,
            fontSize: 13,
            letterSpacing: '0.04em',
            cursor: 'not-allowed',
            marginTop: 8,
          }}
        >
          Lock Prediction (write path ships in UI Phase 2)
        </button>
      </div>
    </div>
  );
}

function FormBlock({ title, children }) {
  return (
    <div className="cat-form-block">
      <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 17, color: '#c8a96e', marginBottom: 14 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function FormField({ label, placeholder }) {
  return (
    <div className="cat-form-row">
      <div className="cat-form-label">{label}</div>
      <input className="cat-form-input" type="text" placeholder={placeholder} disabled />
    </div>
  );
}

function FormFieldArea({ label, placeholder }) {
  return (
    <div className="cat-form-row">
      <div className="cat-form-label">{label}</div>
      <textarea className="cat-form-textarea" placeholder={placeholder} disabled />
    </div>
  );
}
